import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { generateApiErrorPayload } from 'api-client/helpers'
import { listTenants } from 'app/plugins/account/components/userManagement/tenants/new-actions'
import { tenantsSelector } from 'app/plugins/account/components/userManagement/tenants/selectors'
import ListTable from 'core/components/listTable/ListTable'
import Progress from 'core/components/progress/Progress'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useDeleteAction from 'core/hooks/useDeleteAction'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { createNetworkRBACPolicy, deleteNetworkRBACPolicy, listNetworks } from './actions'

const columns = [
  { id: 'id', label: 'UUID', display: false },
  { id: 'name', label: 'Tenant' },
  // This field is only for sorting the rows based on whether they are shared or not
  // It will not be displayed in the table nor shown in the UI
  { id: 'shared', excluded: true },
]

const ShareNetworkWithSpecificTenants = ({ onClose, network }) => {
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const { params, updateParams } = useParams({ selectedTenants: [] })

  // List of existing tenant IDs
  const existingTenantIds = useMemo(
    () => network?.sharedTenants?.map((sharedTenant) => sharedTenant?.target_project) || [],
    [network?.sharedTenants],
  )

  const { reload: reloadNetworks } = useListAction(listNetworks)
  const { loading: loadingTenants } = useListAction(listTenants)
  const tenants = useSelector(tenantsSelector)

  // Filter out the tenant that owns the network
  const filteredTenants = useMemo(() => {
    const filtered = tenants
      .filter((tenant) => tenant.id !== network?.project_id)
      .map((tenant) => ({
        ...tenant,
        // This field is only for sorting the rows based on whether they are shared or not
        // It will not be displayed in the table nor shown in the UI
        shared: existingTenantIds.includes(tenant.id),
      }))

    return filtered
  }, [tenants, network?.project_id, existingTenantIds])

  // Action to remove the policies in bulk
  const {
    bulkRemove,
    reset: resetDelete,
    error: deleteError,
  } = useDeleteAction(deleteNetworkRBACPolicy)
  const {
    bulkUpdate: createRBACPolicy,
    reset: resetCreate,
    error: createError,
  } = useUpdateAction(createNetworkRBACPolicy)

  // Update selected tenants
  useEffect(() => {
    if (tenants.length > 0 && existingTenantIds?.length > 0) {
      updateParams({
        selectedTenants: tenants.filter((tenant) => existingTenantIds?.includes(tenant.id)),
      })
    }
  }, [existingTenantIds, tenants])

  // Submit form
  const submitForm = useCallback(async () => {
    try {
      setSubmitting(true)
      resetDelete()
      resetCreate()
      const selectedTenantIds = params.selectedTenants.flatMap((tenant) => tenant.id)

      // For tenants that are no longer selected, delete the RBAC policy
      if (existingTenantIds?.length > 0) {
        // Skip tenants that are still selected
        const itemsToRemove = network?.sharedTenants
          ?.filter((sharedTenant) => {
            return !selectedTenantIds.includes(sharedTenant?.target_project)
          })
          ?.map((sharedTenant) => ({ policyId: sharedTenant?.policy_id }))

        const response = await bulkRemove(itemsToRemove)

        // Check if any of the deletions failed
        if (response.some((item) => !item.success)) {
          throw new Error('Failed to remove some RBAC policies')
        }
      }

      // For tenants that are newly selected, create the RBAC policy
      if (selectedTenantIds?.length > 0) {
        const itemsToCreate = selectedTenantIds
          // Skip tenants that are already created
          .filter((tenantId) => !existingTenantIds?.includes(tenantId))
          .map((tenantId) => ({
            body: {
              rbac_policy: {
                action: 'access_as_shared',
                object_type: 'network',
                target_tenant: tenantId,
                object_id: network?.id,
                tenant_id: network?.tenant_id,
              },
            },
          }))

        const response = await createRBACPolicy(itemsToCreate)

        // Check if any of the creations failed
        if (response.some((item) => !item.success)) {
          throw new Error('Failed to create some RBAC policies')
        }
      }

      reloadNetworks(true)
      handleClose()
    } catch (error) {
      setError(
        generateApiErrorPayload(
          error,
          'Error Sharing Network',
          'Failed to share network with selected tenants',
        ),
      )
    } finally {
      setSubmitting(false)
    }
  }, [params.selectedTenants, network, updateParams, tenants])

  const handleClose = () => {
    setSubmitting(false)
    setError(null)
    resetDelete()
    resetCreate()
    onClose()
  }

  // Disable submit if no tenants are selected or if all tenants are already selected
  const disableSubmit = useMemo(
    () =>
      (params.selectedTenants.length === 0 ||
        params.selectedTenants.every((tenant) => !!existingTenantIds.includes(tenant.id))) &&
      params.selectedTenants.length === existingTenantIds.length,
    [params.selectedTenants, existingTenantIds, tenants],
  )

  return (
    <ModalForm
      open
      title={`Share Network With Tenants`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={submitting}
      error={createError || deleteError || error}
      initialValues={params}
      submitTitle={'Save Changes'}
      disableSubmit={disableSubmit}
    >
      <Progress
        renderContentOnMount={!loadingTenants}
        loading={loadingTenants}
        message={'Loading Tenants...'}
      >
        <Text component="p" variant="caption1">
          Choose Tenants to Share Network With:
        </Text>
        <br />
        <ListTable
          searchTargets={['name']}
          columns={columns}
          data={filteredTenants}
          rowsPerPage={10}
          selectedRows={params.selectedTenants}
          onSelectedRowsChange={(selectedRows) => {
            updateParams({ selectedTenants: selectedRows })
          }}
          // Sort by shared status (true first, then false), then by name
          orderBy="shared"
          orderDirection="desc"
          noScroll
        />
      </Progress>
    </ModalForm>
  )
}

export default ShareNetworkWithSpecificTenants
